// Cómo se recorren las respuestas gráficas de una conversación.
//
// Los gráficos viven colgados de cada mensaje del asistente (`mensaje.graficos`),
// que es lo natural para dibujar la burbuja. Pero el panel de la derecha los
// muestra de a uno y con "anterior / siguiente", así que necesita la conversación
// APLANADA en una sola lista ordenada. Esa cuenta es pura y vive acá para poder
// testearla sin montar la UI.

import type { ConGraficos, GraficoChat } from './tipos';

/** Todos los gráficos de la conversación, en el orden en que aparecieron. */
export function listarGraficos(mensajes: ConGraficos[]): GraficoChat[] {
  const lista: GraficoChat[] = [];
  for (const m of mensajes) {
    for (const g of m.graficos ?? []) lista.push(g);
  }
  return lista;
}

/** El último gráfico de la conversación (el que conviene abrir solo). */
export function ultimoGrafico(mensajes: ConGraficos[]): GraficoChat | null {
  const lista = listarGraficos(mensajes);
  return lista.length > 0 ? lista[lista.length - 1] : null;
}

export interface UbicacionGrafico {
  grafico: GraficoChat;
  /** Posición 1-based, para mostrar "2 de 5". */
  posicion: number;
  total: number;
  /** Id del anterior / siguiente, o null si es el borde de la lista. */
  anterior: string | null;
  siguiente: string | null;
}

/**
 * Ubica un gráfico dentro de la conversación. `null` si el id no existe —
 * pasa de verdad: el usuario puede tener un gráfico abierto y tocar
 * "Conversación nueva", que borra los mensajes y deja el id colgado. El panel
 * usa ese `null` para cerrarse solo en vez de quedar en blanco.
 */
export function ubicarGrafico(
  mensajes: ConGraficos[],
  id: string | null,
): UbicacionGrafico | null {
  if (!id) return null;
  const lista = listarGraficos(mensajes);
  const indice = lista.findIndex((g) => g.id === id);
  if (indice < 0) return null;
  return {
    grafico: lista[indice],
    posicion: indice + 1,
    total: lista.length,
    anterior: indice > 0 ? lista[indice - 1].id : null,
    siguiente: indice < lista.length - 1 ? lista[indice + 1].id : null,
  };
}

export interface AperturaAutomatica {
  /** Id del gráfico más nuevo de la conversación (null si todavía no hay). */
  idMasNuevo: string | null;
  /** El que el usuario está mirando ahora, si hay alguno. */
  idAbierto: string | null;
  /** Último id que la pantalla ya abrió sola. `undefined` = todavía no evaluó
   *  ninguna vez (recién montada). La diferencia importa: ver la regla 3. */
  idYaOfrecido?: string | null;
}

/**
 * ¿Qué gráfico se abre SOLO en el panel de la derecha? `null` = no tocar nada.
 *
 * Cuando el agente termina de dibujar algo, abrirlo solo es lo correcto: es la
 * respuesta a lo que el usuario acaba de preguntar y aparece al lado de la
 * conversación, no encima. Pero "abrir el más nuevo" a secas PISA la elección
 * del usuario, y ese caso es justamente el camino principal del panel lateral:
 * ahí la tarjeta de un gráfico no lo dibuja (no hay ancho), marca cuál quiere
 * ver y navega a `/chat`. Si al aterrizar la pantalla abriera el más nuevo, el
 * usuario tocaría el gráfico del mensaje 2 y le aparecería el del mensaje 9.
 *
 * Las reglas, en orden:
 *  1. Sin gráficos no hay nada que abrir.
 *  2. Si ya se ofreció ESE id, no se insiste: puede haberlo cerrado a mano y
 *     reabrírselo en cada render sería no dejarlo cerrar nunca.
 *  3. En la PRIMERA evaluación, si ya hay uno abierto, ese ganó — lo eligió el
 *     usuario (tocó la tarjeta en el panel lateral, o la tocó en mobile antes
 *     de agrandar la ventana). Después de la primera, un gráfico con id nuevo
 *     sí toma el panel aunque haya otro abierto: recién llegó y es la respuesta
 *     al último mensaje.
 */
export function graficoQueSeAbreSolo(a: AperturaAutomatica): string | null {
  if (!a.idMasNuevo) return null;
  if (a.idYaOfrecido === a.idMasNuevo) return null;
  const primeraEvaluacion = a.idYaOfrecido === undefined;
  if (primeraEvaluacion && a.idAbierto) return null;
  return a.idMasNuevo;
}
